import { Box, Typography, Button } from "@mui/material";
import { LockKeyhole } from "lucide-react";

export interface DelegationDetail {
  name: string;
  value: string;
  detailType?: string;
  entityType?: string;
}

export interface DetailsByType {
  normal: DelegationDetail[];
  secret: DelegationDetail[];
  document: DelegationDetail[];
}

export const separateDetailsByType = (
  details: DelegationDetail[],
): DetailsByType => {
  return {
    normal: details.filter((d) => !d.detailType),
    secret: details.filter(
      (d) => d.detailType === "grant" && d.entityType === "secret",
    ),
    document: details.filter((d) => d.detailType === "document"),
  };
};

export const parseDetails = (
  detailsJson: string | undefined,
): DelegationDetail[] => {
  if (!detailsJson) return [];
  try {
    return JSON.parse(detailsJson);
  } catch {
    return [];
  }
};

export const isPhoneDetail = (detail: Pick<DelegationDetail, "name">) =>
  detail.name.trim().toLowerCase() === "phone number";

interface NormalDetailsProps {
  details: DelegationDetail[];
  variant?: "default" | "small";
  renderValue?: (detail: DelegationDetail) => React.ReactNode;
}

export const NormalDetails = ({
  details,
  variant = "default",
  renderValue,
}: NormalDetailsProps) => {
  if (details.length === 0) return null;

  const isSmall = variant === "small";
  const LabelTypography = isSmall ? "bodySmallHeavy" : "bodyHeavy";
  const ValueTypography = isSmall ? "bodySmall" : "body";

  return (
    <Box sx={{ marginBottom: "16px" }}>
      <Typography
        variant={isSmall ? "bodySmall" : "h5"}
        color={isSmall ? "text.secondary" : undefined}
        sx={{ marginBottom: isSmall ? "4px" : "8px" }}
      >
        Details
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {details.map((detail, index) => (
          <Box key={index} sx={{ display: "flex", gap: "8px" }}>
            <Typography variant={LabelTypography} sx={{ minWidth: "120px" }}>
              {detail.name}
              {isSmall ? ":" : ""}
            </Typography>
            <Typography variant={ValueTypography}>
              {renderValue ? renderValue(detail) : detail.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface SecretDetailsProps {
  details: DelegationDetail[];
  variant?: "default" | "small";
  onReveal?: (secretRef: string) => void;
  showRevealButton?: boolean;
}

export const SecretDetails = ({
  details,
  variant = "default",
  onReveal,
  showRevealButton = true,
}: SecretDetailsProps) => {
  if (details.length === 0) return null;

  const isSmall = variant === "small";
  const LabelTypography = isSmall ? "bodySmallHeavy" : "bodyHeavy";
  const ValueTypography = isSmall ? "bodySmall" : "body";
  const iconSize = isSmall ? 14 : 16;

  return (
    <Box sx={{ marginBottom: "16px" }}>
      <Typography
        variant={isSmall ? "bodySmall" : "h5"}
        color={isSmall ? "text.secondary" : undefined}
        sx={{ marginBottom: isSmall ? "4px" : "8px" }}
      >
        Secrets
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {details.map((detail, index) => (
          <Box
            key={index}
            sx={{ display: "flex", gap: "8px", alignItems: "center" }}
          >
            <Typography variant={LabelTypography} sx={{ minWidth: "120px" }}>
              {detail.name}
              {isSmall ? ":" : ""}
            </Typography>
            <Typography
              variant={ValueTypography}
              sx={{ color: "text.secondary" }}
            >
              ••••••••
            </Typography>
            {showRevealButton && detail.value && onReveal && (
              <Button
                size="small"
                variant="text"
                startIcon={<LockKeyhole size={iconSize} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onReveal(detail.value);
                }}
                sx={{
                  minWidth: 0,
                  padding: "2px 6px",
                  color: "primary.main",
                  textTransform: "none",
                  "& .MuiButton-startIcon": {
                    marginRight: "4px",
                  },
                }}
              >
                Reveal
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface AllDetailsProps {
  detailsJson: string | undefined;
  variant?: "default" | "small";
  onRevealSecret?: (secretRef: string) => void;
  showRevealButton?: boolean;
  renderNormalValue?: (detail: DelegationDetail) => React.ReactNode;
}

export const AllDetails = ({
  detailsJson,
  variant = "default",
  onRevealSecret,
  showRevealButton = true,
  renderNormalValue,
}: AllDetailsProps) => {
  const details = parseDetails(detailsJson);
  const { normal, secret } = separateDetailsByType(details);

  return (
    <>
      <NormalDetails
        details={normal}
        variant={variant}
        renderValue={renderNormalValue}
      />
      <SecretDetails
        details={secret}
        variant={variant}
        onReveal={onRevealSecret}
        showRevealButton={showRevealButton}
      />
    </>
  );
};
